import { SHAPES, TYPES, FILL_TYPES } from '../../../render/constants';
import { renderNodes } from '../../../lib/gen/render/render';
import { wrapText } from '../../../render/text';
import { deserializePoints, deserializeDashArray } from '../../../render/line';
import { resolveStrokeWidth, resolveStrokeColor } from '../../../render/utils/stroke';
import { resolveFillType } from '../../../render/utils/background';

/**
 * Escape special characters for SVG/XML
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeXml(str) {
  if (!str || typeof str !== 'string') return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Round a number to a fixed precision
 * @param {number} value - The value to round
 * @param {number} precision - Number of decimal places (default: 2)
 * @returns {number} The rounded value
 */
function round(value, precision = 2) {
  if (typeof value !== 'number' || !isFinite(value)) return 0;
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
}

/**
 * Check if a color string contains a gradient definition.
 */
function isGradient(colorStr) {
  return colorStr && typeof colorStr === 'string' && colorStr.includes('gradient');
}

/**
 * Check if a fill color is transparent
 */
function isTransparentFill(fill) {
  if (!fill || fill === 'none' || fill === 'transparent') return true;

  const rgbaMatch = fill.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch && rgbaMatch[4] !== undefined) {
    const alpha = parseFloat(rgbaMatch[4]);
    if (alpha === 0) return true;
  }

  return false;
}

/**
 * Parse a color string and return a valid SVG color value.
 * @param {string} colorStr - The color string to parse
 * @returns {string} A valid SVG color value
 */
function parseColor(colorStr) {
  if (!colorStr || colorStr === 'none' || colorStr === 'transparent') {
    return 'none';
  }

  // Handle hex colors
  if (colorStr.startsWith('#')) {
    return colorStr;
  }

  // Handle rgb/rgba colors - SVG supports these directly
  if (colorStr.startsWith('rgb')) {
    return colorStr;
  }

  // Handle hsl/hsla colors - SVG supports these directly
  if (colorStr.startsWith('hsl')) {
    return colorStr;
  }

  // Return as-is for other formats (named colors, etc.)
  return colorStr;
}

/**
 * Extract opacity from rgba color
 * @param {string} colorStr - Color string
 * @returns {number} Opacity value between 0 and 1
 */
function extractOpacity(colorStr) {
  if (!colorStr) return 1;

  const rgbaMatch = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch && rgbaMatch[4] !== undefined) {
    return parseFloat(rgbaMatch[4]);
  }

  return 1;
}

/**
 * Normalize radius into four corner values [topLeft, topRight, bottomRight, bottomLeft].
 */
function parseRadiusCorners(radius) {
  if (!radius) return [0, 0, 0, 0];

  if (typeof radius === 'number') {
    const v = radius > 0 ? radius : 0;
    return [v, v, v, v];
  }

  if (typeof radius === 'string') {
    const parts = radius
      .trim()
      .split(/\s+/)
      .map((p) => {
        const cleaned = p.replace(/px$/i, '');
        const val = parseFloat(cleaned);
        return !isNaN(val) && val > 0 ? val : 0;
      });

    if (parts.length === 1) {
      return [parts[0], parts[0], parts[0], parts[0]];
    } else if (parts.length === 2) {
      return [parts[0], parts[1], parts[0], parts[1]];
    } else if (parts.length === 3) {
      return [parts[0], parts[1], parts[2], parts[1]];
    } else {
      return [parts[0], parts[1], parts[2], parts[3]];
    }
  }

  return [0, 0, 0, 0];
}

/**
 * Get numeric radius value
 */
function getNumericRadius(node) {
  const radius = node && node.radius;
  if (!radius) return 0;

  if (typeof radius === 'number') {
    return radius > 0 ? radius : 0;
  }

  if (typeof radius === 'string') {
    const first = radius.trim().split(/\s+/)[0] || '';
    const cleaned = first.replace(/px$/i, '');
    const value = parseFloat(cleaned);
    if (!isNaN(value) && value > 0) {
      return value;
    }
  }

  return 0;
}

/**
 * Generate unique ID for SVG elements
 */
let idCounter = 0;
function generateId(prefix = 'svg') {
  return `${prefix}_${++idCounter}`;
}

/**
 * Reset ID counter (useful for generating consistent SVGs)
 */
function resetIdCounter() {
  idCounter = 0;
}

/**
 * Parse CSS gradient and convert to SVG gradient definition
 * @param {string} gradientStr - CSS gradient string
 * @param {string} id - Unique ID for the gradient
 * @param {object} bounds - Bounding box { w, h }
 * @returns {object} { def: string, ref: string } - SVG defs content and fill reference
 */
function parseGradientToSVG(gradientStr, id, bounds) {
  if (!gradientStr || !isGradient(gradientStr)) {
    return null;
  }

  // Parse linear-gradient
  const linearMatch = gradientStr.match(
    /linear-gradient\(\s*(\d+(?:\.\d+)?(?:deg|rad|turn)?|to\s+\w+(?:\s+\w+)?)\s*,\s*(.+)\)/i
  );
  if (linearMatch) {
    let angle = 180; // default: top to bottom
    const angleStr = linearMatch[1];

    if (angleStr.includes('deg')) {
      angle = parseFloat(angleStr);
    } else if (angleStr.includes('rad')) {
      angle = parseFloat(angleStr) * (180 / Math.PI);
    } else if (angleStr.includes('turn')) {
      angle = parseFloat(angleStr) * 360;
    } else if (angleStr.startsWith('to ')) {
      // Handle direction keywords
      const dir = angleStr.replace('to ', '').trim();
      const dirMap = {
        top: 0,
        right: 90,
        bottom: 180,
        left: 270,
        'top right': 45,
        'right top': 45,
        'bottom right': 135,
        'right bottom': 135,
        'bottom left': 225,
        'left bottom': 225,
        'top left': 315,
        'left top': 315,
      };
      angle = dirMap[dir] ?? 180;
    }

    // Convert angle to SVG coordinates
    const rad = ((angle - 90) * Math.PI) / 180;
    const x1 = round(50 - Math.cos(rad) * 50);
    const y1 = round(50 - Math.sin(rad) * 50);
    const x2 = round(50 + Math.cos(rad) * 50);
    const y2 = round(50 + Math.sin(rad) * 50);

    // Parse color stops
    const stopsStr = linearMatch[2];
    const stops = parseColorStops(stopsStr);

    const stopElements = stops
      .map(
        (stop) =>
          `<stop offset="${stop.offset}%" stop-color="${escapeXml(stop.color)}"${stop.opacity < 1 ? ` stop-opacity="${stop.opacity}"` : ''}/>`
      )
      .join('');

    const def = `<linearGradient id="${id}" x1="${x1}%" y1="${y1}%" x2="${x2}%" y2="${y2}%">${stopElements}</linearGradient>`;

    return { def, ref: `url(#${id})` };
  }

  // Parse radial-gradient
  const radialMatch = gradientStr.match(/radial-gradient\(\s*(.+)\)/i);
  if (radialMatch) {
    const content = radialMatch[1];
    const stops = parseColorStops(content);

    const stopElements = stops
      .map(
        (stop) =>
          `<stop offset="${stop.offset}%" stop-color="${escapeXml(stop.color)}"${stop.opacity < 1 ? ` stop-opacity="${stop.opacity}"` : ''}/>`
      )
      .join('');

    const def = `<radialGradient id="${id}" cx="50%" cy="50%" r="50%">${stopElements}</radialGradient>`;

    return { def, ref: `url(#${id})` };
  }

  return null;
}

/**
 * Parse color stops from gradient string
 * @param {string} stopsStr - Color stops portion of gradient
 * @returns {Array} Array of { color, offset, opacity }
 */
function parseColorStops(stopsStr) {
  const stops = [];
  // Split by comma but not within parentheses
  const parts = stopsStr.split(/,(?![^(]*\))/);

  let lastOffset = 0;
  const rawStops = [];

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    // Skip shape/size keywords for radial gradients
    if (
      trimmed.startsWith('circle') ||
      trimmed.startsWith('ellipse') ||
      trimmed.includes('at ') ||
      trimmed === 'closest-side' ||
      trimmed === 'farthest-side' ||
      trimmed === 'closest-corner' ||
      trimmed === 'farthest-corner'
    ) {
      continue;
    }

    // Match color and optional position
    const colorMatch = trimmed.match(/^(.+?)(?:\s+(\d+(?:\.\d+)?%?))?$/);
    if (colorMatch) {
      const color = colorMatch[1].trim();
      let offset = colorMatch[2];

      if (offset) {
        offset = parseFloat(offset.replace('%', ''));
      } else {
        offset = null; // Will be calculated later
      }

      const opacity = extractOpacity(color);
      rawStops.push({ color: parseColor(color), offset, opacity });
    }
  }

  // Calculate missing offsets
  if (rawStops.length > 0) {
    // First stop defaults to 0%
    if (rawStops[0].offset === null) {
      rawStops[0].offset = 0;
    }
    // Last stop defaults to 100%
    if (rawStops[rawStops.length - 1].offset === null) {
      rawStops[rawStops.length - 1].offset = 100;
    }

    // Fill in missing offsets with linear interpolation
    let prevIndex = 0;
    for (let i = 1; i < rawStops.length; i++) {
      if (rawStops[i].offset !== null) {
        const gap = i - prevIndex;
        if (gap > 1) {
          const startOffset = rawStops[prevIndex].offset;
          const endOffset = rawStops[i].offset;
          const step = (endOffset - startOffset) / gap;
          for (let j = prevIndex + 1; j < i; j++) {
            rawStops[j].offset = startOffset + step * (j - prevIndex);
          }
        }
        prevIndex = i;
      }
    }

    return rawStops.map((s) => ({
      color: s.color,
      offset: round(s.offset),
      opacity: s.opacity,
    }));
  }

  return [
    { color: '#000000', offset: 0, opacity: 1 },
    { color: '#ffffff', offset: 100, opacity: 1 },
  ];
}

/**
 * Build SVG style attributes from node properties
 * @param {object} node - The node
 * @param {object} options - Options { includeFill, includeStroke }
 * @returns {object} { attrs: string, defs: string[] }
 */
function buildStyleAttrs(node, options = {}) {
  const { includeFill = true, includeStroke = true } = options;
  const attrs = [];
  const defs = [];

  // Handle fill
  if (includeFill) {
    const fill = node.fill;
    if (isTransparentFill(fill)) {
      attrs.push('fill="none"');
    } else if (isGradient(fill)) {
      const gradientId = generateId('grad');
      const gradient = parseGradientToSVG(fill, gradientId, { w: node.w, h: node.h });
      if (gradient) {
        defs.push(gradient.def);
        attrs.push(`fill="${gradient.ref}"`);
      } else {
        attrs.push('fill="none"');
      }
    } else if (fill) {
      const color = parseColor(fill);
      const opacity = extractOpacity(fill);
      attrs.push(`fill="${escapeXml(color)}"`);
      if (opacity < 1) {
        attrs.push(`fill-opacity="${round(opacity)}"`);
      }
    }
  }

  // Handle stroke
  if (includeStroke) {
    const strokeWidths = resolveStrokeWidth(node.strokeWidth);
    const strokeColors = resolveStrokeColor(node.stroke);

    if (!strokeColors.isEmpty && strokeColors.value && strokeWidths.value > 0) {
      const strokeColor = parseColor(strokeColors.value);
      const strokeOpacity = extractOpacity(strokeColors.value);

      attrs.push(`stroke="${escapeXml(strokeColor)}"`);
      attrs.push(`stroke-width="${round(strokeWidths.value)}"`);

      if (strokeOpacity < 1) {
        attrs.push(`stroke-opacity="${round(strokeOpacity)}"`);
      }

      // Handle stroke dash
      if (node.strokeDasharray) {
        attrs.push(`stroke-dasharray="${escapeXml(node.strokeDasharray)}"`);
      }

      // Handle stroke line cap
      if (node.strokeLinecap || node.lineCap) {
        attrs.push(`stroke-linecap="${node.strokeLinecap || node.lineCap}"`);
      }

      // Handle stroke line join
      if (node.strokeLinejoin || node.lineJoin) {
        attrs.push(`stroke-linejoin="${node.strokeLinejoin || node.lineJoin}"`);
      }
    }
  }

  // Handle opacity
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    attrs.push(`opacity="${round(node.opacity)}"`);
  }

  return { attrs: attrs.join(' '), defs };
}

/**
 * Build SVG transform attribute from node
 * @param {object} node - The node
 * @param {number} originX - Origin X offset
 * @param {number} originY - Origin Y offset
 * @returns {string} Transform attribute value
 */
function buildTransform(node, originX = 0, originY = 0) {
  const transforms = [];

  // Position transform
  const x = round((node.x || 0) - originX);
  const y = round((node.y || 0) - originY);
  if (x !== 0 || y !== 0) {
    transforms.push(`translate(${x}, ${y})`);
  }

  // Rotation transform
  if (node.rotation) {
    const cx = round((node.w || 0) / 2);
    const cy = round((node.h || 0) / 2);
    transforms.push(`rotate(${round(node.rotation)}, ${cx}, ${cy})`);
  }

  return transforms.length > 0 ? `transform="${transforms.join(' ')}"` : '';
}

/**
 * Convert nodes to SVG string
 * @param {object} store - The store context
 * @param {Array|object} nodes - Node or array of nodes to convert
 * @param {object} options - Export options
 * @returns {Promise<string>} SVG string
 */
export async function node2SVG(store, nodes, { onProgress } = {}) {
  resetIdCounter();

  if (!Array.isArray(nodes)) {
    nodes = [nodes];
  }

  const frames = nodes.filter((n) => n.type === TYPES.frame);
  const otherNodes = nodes.filter((n) => n.type !== TYPES.frame);

  let layoutW = 1280;
  let layoutH = 720;

  if (frames.length > 0) {
    layoutW = frames[0].w;
    layoutH = frames[0].h;
  } else if (otherNodes.length > 0) {
    const rect = store.api.getNodesRegion(otherNodes);
    layoutW = rect.w;
    layoutH = rect.h;
  }

  const svgDefs = [];
  const svgContent = [];

  // Calculate total nodes for progress
  const frameChildrenCounts = frames.map((frame) => {
    const children = store.api.getChildren(frame.id) || [];
    return children.length || 1;
  });
  const totalNodes = frameChildrenCounts.reduce((sum, c) => sum + c, 0) + otherNodes.length;
  let processedNodes = 0;

  // Render frames
  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    const frameBox = store.api.getNodeBox(frame);

    // Render frame background
    const bgResult = await renderFrameBackground(store, frame, layoutW, layoutH);
    if (bgResult.defs) svgDefs.push(...bgResult.defs);
    if (bgResult.content) svgContent.push(bgResult.content);

    // Render children
    const children = store.api.getChildren(frame.id) || [];
    for (const child of children) {
      const result = await renderNode(store, child, { originX: frameBox.x, originY: frameBox.y });
      if (result.defs) svgDefs.push(...result.defs);
      if (result.content) svgContent.push(result.content);

      processedNodes++;
      if (onProgress) {
        onProgress(processedNodes / totalNodes, i + 1);
      }
    }

    if (children.length === 0) {
      processedNodes++;
      if (onProgress) {
        onProgress(processedNodes / totalNodes, i + 1);
      }
    }
  }

  // Render other nodes (not in frames)
  if (otherNodes.length > 0) {
    const rect = store.api.getNodesRegion(otherNodes);
    for (const node of otherNodes) {
      const result = await renderNode(store, node, { originX: rect.x, originY: rect.y });
      if (result.defs) svgDefs.push(...result.defs);
      if (result.content) svgContent.push(result.content);

      processedNodes++;
      if (onProgress) {
        onProgress(processedNodes / totalNodes, frames.length + 1);
      }
    }
  }

  // Build final SVG
  const defsSection = svgDefs.length > 0 ? `<defs>${svgDefs.join('')}</defs>` : '';

  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" 
     width="${round(layoutW)}" height="${round(layoutH)}" 
     viewBox="0 0 ${round(layoutW)} ${round(layoutH)}">
  ${defsSection}
  ${svgContent.join('\n  ')}
</svg>`;

  return svg;
}

/**
 * Render frame background
 */
async function renderFrameBackground(store, frame, layoutW, layoutH) {
  const fill = frame.fill;
  const defs = [];
  let content = '';

  if (isTransparentFill(fill)) {
    // Transparent background - add white rect
    content = `<rect x="0" y="0" width="${round(layoutW)}" height="${round(layoutH)}" fill="#ffffff"/>`;
    return { defs, content };
  }

  const fillType = resolveFillType(fill);

  // Complex backgrounds need image rendering
  const needsImageRendering = [
    FILL_TYPES.GRADIENT,
    FILL_TYPES.IMAGE,
    FILL_TYPES.DYNAMIC,
    FILL_TYPES.PATTERN,
  ].includes(fillType);

  if (needsImageRendering && fillType !== FILL_TYPES.GRADIENT) {
    try {
      const bgNode = {
        ...frame,
        type: TYPES.shape,
        shape: SHAPES.rect,
        x: 0,
        y: 0,
        w: layoutW,
        h: layoutH,
      };
      const dataUrl = await renderNodes(store, {
        nodes: [bgNode],
        contentType: 'image/png',
      });
      if (dataUrl) {
        content = `<image x="0" y="0" width="${round(layoutW)}" height="${round(layoutH)}" href="${dataUrl}"/>`;
        return { defs, content };
      }
    } catch (err) {
      console.warn('Failed to render complex background as image:', err);
    }
  }

  // Handle gradient
  if (fillType === FILL_TYPES.GRADIENT) {
    const gradientId = generateId('bgGrad');
    const gradient = parseGradientToSVG(fill, gradientId, { w: layoutW, h: layoutH });
    if (gradient) {
      defs.push(gradient.def);
      content = `<rect x="0" y="0" width="${round(layoutW)}" height="${round(layoutH)}" fill="${gradient.ref}"/>`;
      return { defs, content };
    }
  }

  // Solid color
  const color = parseColor(fill) || '#ffffff';
  content = `<rect x="0" y="0" width="${round(layoutW)}" height="${round(layoutH)}" fill="${escapeXml(color)}"/>`;

  return { defs, content };
}

/**
 * Render a single node to SVG
 */
async function renderNode(store, node, { originX = 0, originY = 0 } = {}) {
  const box = store.api.getNodeBox(node);
  const x = round(box.x - originX);
  const y = round(box.y - originY);
  const w = round(box.w);
  const h = round(box.h);

  const context = { x, y, w, h, originX, originY };

  switch (node.type) {
    case TYPES.text:
      return renderText(store, node, context);
    case TYPES.image:
      return await renderImage(store, node, context);
    case TYPES.shape:
      return await renderShape(store, node, context);
    case TYPES.line:
      return renderLine(store, node, context);
    case TYPES.path:
      return renderPath(store, node, context);
    case TYPES.group:
      return await renderGroup(store, node, context);
    case TYPES.frame:
      return await renderFrame(store, node, context);
    default:
      return { defs: [], content: '' };
  }
}

/**
 * Render text node
 */
function renderText(store, node, { x, y, w, h }) {
  const defs = [];
  const text = node.text || '';

  // Text styling
  const fontSize = node.fontSize || 16;
  const fontFamily = node.fontFamily || 'Arial';
  const fontWeight = node.fontWeight || 'normal';
  const fontStyle = node.fontStyle || 'normal';
  const textAlign = node.textAlign || 'left';
  const lineHeight = node.lineHeight || 1.2;
  const letterSpacing = node.letterSpacing || 0;

  // Use wrapText to get properly wrapped lines
  let wrappedLines = [];
  let lineHeightPx = fontSize * lineHeight;
  try {
    const wrapResult = wrapText(node, text);
    if (wrapResult && wrapResult.lines && wrapResult.lines.length > 0) {
      // wrapResult.lines is an array of grapheme arrays, convert to strings
      wrappedLines = wrapResult.lines.map((lineGraphemes) => {
        // Each line is an array of graphemes (characters), join them
        return Array.isArray(lineGraphemes) ? lineGraphemes.join('') : String(lineGraphemes);
      });
      // Use the lineHeight from font settings if available
      // Note: fontSettings.lineHeight is a ratio (e.g., 1.35), not pixels
      // We need to multiply by fontSize to get actual pixel height
      if (wrapResult.fontSettings && wrapResult.fontSettings.lineHeight) {
        lineHeightPx = fontSize * wrapResult.fontSettings.lineHeight;
      }
    }
  } catch (err) {
    console.warn('Failed to wrap text for SVG export:', err);
  }

  // Fallback to simple newline split if wrapText fails or returns empty
  if (wrappedLines.length === 0) {
    wrappedLines = text.split('\n');
  }

  // Calculate text anchor based on alignment
  let textAnchor = 'start';
  let textX = x;
  if (textAlign === 'center') {
    textAnchor = 'middle';
    textX = x + w / 2;
  } else if (textAlign === 'right') {
    textAnchor = 'end';
    textX = x + w;
  }

  // Calculate vertical position based on alignment
  // For multi-line text, we need to calculate the total text height first
  const totalTextHeight = wrappedLines.length * lineHeightPx;
  let startY = y;

  if (node.alignItems === 'center') {
    // Center vertically: start from middle minus half of total height
    startY = y + (h - totalTextHeight) / 2;
  } else if (node.alignItems === 'flex-end') {
    // Bottom align: start from bottom minus total height
    startY = y + h - totalTextHeight;
  }
  // For top alignment (default), startY remains y

  // Build style attributes
  const styleAttrs = [];
  styleAttrs.push(`font-size="${round(fontSize)}px"`);
  styleAttrs.push(`font-family="${escapeXml(fontFamily)}"`);

  if (fontWeight !== 'normal' && fontWeight !== '400') {
    styleAttrs.push(`font-weight="${fontWeight}"`);
  }
  if (fontStyle !== 'normal') {
    styleAttrs.push(`font-style="${fontStyle}"`);
  }
  if (letterSpacing !== 0) {
    styleAttrs.push(`letter-spacing="${round(letterSpacing)}px"`);
  }

  // Handle fill color
  const fill = node.fill;
  if (isGradient(fill)) {
    const gradientId = generateId('textGrad');
    const gradient = parseGradientToSVG(fill, gradientId, { w, h });
    if (gradient) {
      defs.push(gradient.def);
      styleAttrs.push(`fill="${gradient.ref}"`);
    }
  } else if (fill) {
    const color = parseColor(fill);
    const opacity = extractOpacity(fill);
    styleAttrs.push(`fill="${escapeXml(color)}"`);
    if (opacity < 1) {
      styleAttrs.push(`fill-opacity="${round(opacity)}"`);
    }
  }

  // Handle text decoration
  if (node.textDecoration === 'underline') {
    styleAttrs.push('text-decoration="underline"');
  } else if (node.textDecoration === 'line-through') {
    styleAttrs.push('text-decoration="line-through"');
  }

  // Handle opacity
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    styleAttrs.push(`opacity="${round(node.opacity)}"`);
  }

  // Handle background (render as rect behind text)
  let bgRect = '';
  if (node.background && !isTransparentFill(node.background)) {
    const bgColor = parseColor(node.background);
    const radius = getNumericRadius(node);
    if (radius > 0) {
      // Clamp radius to half of the smaller dimension to avoid ellipse effect
      const maxRadius = Math.min(w / 2, h / 2);
      const clampedRadius = Math.min(radius, maxRadius);
      bgRect = `<rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${round(h)}" fill="${escapeXml(bgColor)}" rx="${round(clampedRadius)}"/>`;
    } else {
      bgRect = `<rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${round(h)}" fill="${escapeXml(bgColor)}"/>`;
    }
  }

  // Render text lines
  let textContent;
  if (wrappedLines.length === 1) {
    // Single line - use dominant-baseline for vertical positioning
    const dominantBaseline = 'hanging';
    textContent = `<text x="${round(textX)}" y="${round(startY)}" text-anchor="${textAnchor}" dominant-baseline="${dominantBaseline}" ${styleAttrs.join(' ')}>${escapeXml(wrappedLines[0])}</text>`;
  } else {
    // Multi-line text using tspan elements
    // Use dominant-baseline="hanging" so y coordinate represents the top of the text
    const tspans = wrappedLines
      .map((line, i) => {
        // First line: no dy offset, subsequent lines: offset by lineHeight
        const dy = i === 0 ? 0 : lineHeightPx;
        return `<tspan x="${round(textX)}" dy="${i === 0 ? '0' : round(dy)}">${escapeXml(line)}</tspan>`;
      })
      .join('');
    textContent = `<text x="${round(textX)}" y="${round(startY)}" text-anchor="${textAnchor}" dominant-baseline="hanging" ${styleAttrs.join(' ')}>${tspans}</text>`;
  }

  const content = bgRect ? `<g>${bgRect}${textContent}</g>` : textContent;

  return { defs, content };
}

/**
 * Render image node
 */
async function renderImage(store, node, { x, y, w, h }) {
  const defs = [];
  let src = node.src;

  if (!src) {
    return { defs, content: '' };
  }

  // Resolve asset URL
  try {
    const assetProvider = store?.providers?.asset;
    if (assetProvider && typeof assetProvider.getAsset === 'function') {
      src = assetProvider.getAsset(src);
    }
  } catch {
    // ignore, fallback to original src
  }

  // Convert to data URL if needed
  if (!src.startsWith('data:')) {
    try {
      const blob = await fetch(src).then((res) => res.blob());
      src = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      // If fetch fails, use original src
    }
  }

  // Handle SVG images - render as PNG to avoid nesting issues
  if (src && src.includes('data:image/svg+xml')) {
    try {
      const dataUrl = await renderNodes(store, {
        nodes: [node],
        dpr: 4,
        contentType: 'image/png',
      });
      if (dataUrl) {
        src = dataUrl;
      }
    } catch (err) {
      console.warn('Failed to render SVG as PNG:', err);
    }
  }

  // Build image element
  const attrs = [
    `x="${round(x)}"`,
    `y="${round(y)}"`,
    `width="${round(w)}"`,
    `height="${round(h)}"`,
  ];

  if (src) {
    attrs.push(`href="${src}"`);
  }

  // Handle opacity
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    attrs.push(`opacity="${round(node.opacity)}"`);
  }

  // Handle border radius with clip path
  const radius = getNumericRadius(node);
  if (radius > 0) {
    const clipId = generateId('imgClip');
    const clipPath = `<clipPath id="${clipId}"><rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${round(h)}" rx="${round(radius)}" ry="${round(radius)}"/></clipPath>`;
    defs.push(clipPath);
    attrs.push(`clip-path="url(#${clipId})"`);
  }

  // Handle object fit
  if (node.fit && node.fit !== 'fill') {
    attrs.push(
      `preserveAspectRatio="${node.fit === 'cover' ? 'xMidYMid slice' : 'xMidYMid meet'}"`
    );
  }

  const content = `<image ${attrs.join(' ')}/>`;

  return { defs, content };
}

/**
 * Render shape node
 */
async function renderShape(store, node, { x, y, w, h }) {
  const defs = [];
  const nodeShape = node.shape || SHAPES.rect;

  // Check if shape needs image fallback
  const needsImageFallback =
    isGradient(node.fill) || ![SHAPES.rect, SHAPES.ellipse].includes(nodeShape);

  if (needsImageFallback && nodeShape !== SHAPES.rect && nodeShape !== SHAPES.ellipse) {
    try {
      const dataUrl = await renderNodes(store, {
        nodes: [node],
        contentType: 'image/png',
      });
      if (dataUrl) {
        const attrs = [
          `x="${round(x)}"`,
          `y="${round(y)}"`,
          `width="${round(w)}"`,
          `height="${round(h)}"`,
          `href="${dataUrl}"`,
        ];
        if (typeof node.opacity === 'number' && node.opacity < 1) {
          attrs.push(`opacity="${round(node.opacity)}"`);
        }
        return { defs, content: `<image ${attrs.join(' ')}/>` };
      }
    } catch (err) {
      console.warn('Failed to render shape as image:', err);
    }
  }

  // Build style attributes
  const { attrs: styleAttrs, defs: styleDefs } = buildStyleAttrs(node);
  defs.push(...styleDefs);

  let content = '';

  if (nodeShape === SHAPES.ellipse) {
    // Ellipse
    const cx = round(x + w / 2);
    const cy = round(y + h / 2);
    const rx = round(w / 2);
    const ry = round(h / 2);
    content = `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" ${styleAttrs}/>`;
  } else {
    // Rectangle (default)
    const radius = getNumericRadius(node);
    const attrs = [
      `x="${round(x)}"`,
      `y="${round(y)}"`,
      `width="${round(w)}"`,
      `height="${round(h)}"`,
    ];

    if (radius > 0) {
      const maxRadius = Math.min(w, h) / 2;
      const clampedRadius = Math.min(radius, maxRadius);
      attrs.push(`rx="${round(clampedRadius)}"`);
      attrs.push(`ry="${round(clampedRadius)}"`);
    }

    content = `<rect ${attrs.join(' ')} ${styleAttrs}/>`;
  }

  return { defs, content };
}

/**
 * Render path node
 */
function renderPath(store, node, { x, y, w, h }) {
  const defs = [];
  const pathD = node.d || node.path || '';

  if (!pathD) {
    return { defs, content: '' };
  }

  // Build style attributes
  const { attrs: styleAttrs, defs: styleDefs } = buildStyleAttrs(node);
  defs.push(...styleDefs);

  // Wrap path in a group with transform for positioning
  const content = `<g transform="translate(${round(x)}, ${round(y)})"><path d="${escapeXml(pathD)}" ${styleAttrs}/></g>`;

  return { defs, content };
}

/**
 * Render line node
 */
function renderLine(store, node, { x, y, w, h }) {
  const defs = [];
  const points = deserializePoints(node.points);

  if (!points || points.length < 2) {
    return { defs, content: '' };
  }

  // Convert relative coordinates to absolute
  const absolutePoints = points.map((p) => ({
    x: round(x + p.x * w),
    y: round(y + p.y * h),
  }));

  // Build polyline points string
  const pointsStr = absolutePoints.map((p) => `${p.x},${p.y}`).join(' ');

  // Build style attributes
  const attrs = [];
  attrs.push(`points="${pointsStr}"`);
  attrs.push('fill="none"');

  // Stroke color
  const strokeColor = parseColor(node.fill || '#000000');
  const strokeOpacity = extractOpacity(node.fill || '#000000');
  attrs.push(`stroke="${escapeXml(strokeColor)}"`);
  if (strokeOpacity < 1) {
    attrs.push(`stroke-opacity="${round(strokeOpacity)}"`);
  }

  // Stroke width
  attrs.push(`stroke-width="${round(node.size || 1)}"`);

  // Line cap and join
  if (node.lineCap) {
    attrs.push(`stroke-linecap="${node.lineCap}"`);
  }
  if (node.lineJoin) {
    attrs.push(`stroke-linejoin="${node.lineJoin}"`);
  }

  // Dash array
  if (node.strokeStyle === 'dashed' && node.dashArray) {
    const dashArray = deserializeDashArray(node.dashArray);
    if (dashArray.length > 0) {
      attrs.push(`stroke-dasharray="${dashArray.join(' ')}"`);
    }
  }

  // Opacity
  if (typeof node.opacity === 'number' && node.opacity < 1) {
    attrs.push(`opacity="${round(node.opacity)}"`);
  }

  // Handle markers
  let markerDefs = '';
  if (node.startMarker && node.startMarker !== 'none') {
    const markerId = generateId('markerStart');
    markerDefs += buildMarkerDef(markerId, node.startMarker, node.markerSize || 10, strokeColor);
    attrs.push(`marker-start="url(#${markerId})"`);
    defs.push(markerDefs);
  }
  if (node.endMarker && node.endMarker !== 'none') {
    const markerId = generateId('markerEnd');
    markerDefs = buildMarkerDef(markerId, node.endMarker, node.markerSize || 10, strokeColor, true);
    attrs.push(`marker-end="url(#${markerId})"`);
    defs.push(markerDefs);
  }

  const content = `<polyline ${attrs.join(' ')}/>`;

  return { defs, content };
}

/**
 * Build SVG marker definition
 */
function buildMarkerDef(id, type, size, color, isEnd = false) {
  const halfSize = size / 2;

  switch (type) {
    case 'arrow': {
      const orient = isEnd ? 'auto' : 'auto-start-reverse';
      return `<marker id="${id}" markerWidth="${size}" markerHeight="${size}" refX="${size * 0.6}" refY="${halfSize}" orient="${orient}">
        <polygon points="0,0 ${size},${halfSize} 0,${size}" fill="${escapeXml(color)}"/>
      </marker>`;
    }
    case 'circle': {
      const radius = size * 0.4;
      return `<marker id="${id}" markerWidth="${size}" markerHeight="${size}" refX="${halfSize}" refY="${halfSize}">
        <circle cx="${halfSize}" cy="${halfSize}" r="${round(radius)}" fill="${escapeXml(color)}"/>
      </marker>`;
    }
    case 'square': {
      const squareSize = size * 0.8;
      const offset = (size - squareSize) / 2;
      return `<marker id="${id}" markerWidth="${size}" markerHeight="${size}" refX="${halfSize}" refY="${halfSize}">
        <rect x="${round(offset)}" y="${round(offset)}" width="${round(squareSize)}" height="${round(squareSize)}" fill="${escapeXml(color)}"/>
      </marker>`;
    }
    default:
      return '';
  }
}

/**
 * Render group node
 */
async function renderGroup(store, node, { originX, originY }) {
  const defs = [];
  const children = store.api.getChildren(node.id) || [];
  const childContents = [];

  for (const child of children) {
    const result = await renderNode(store, child, { originX, originY });
    if (result.defs) defs.push(...result.defs);
    if (result.content) childContents.push(result.content);
  }

  // Build group attributes
  const attrs = [];

  // Handle opacity
  if (typeof node.opacity === 'number' && node.opacity < 1) {
    attrs.push(`opacity="${round(node.opacity)}"`);
  }

  // Handle clip for overflow hidden
  if (node.overflow === 'hidden') {
    const box = store.api.getNodeBox(node);
    const x = round(box.x - originX);
    const y = round(box.y - originY);
    const w = round(box.w);
    const h = round(box.h);
    const radius = getNumericRadius(node);

    const clipId = generateId('groupClip');
    if (radius > 0) {
      defs.push(
        `<clipPath id="${clipId}"><rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${round(radius)}" ry="${round(radius)}"/></clipPath>`
      );
    } else {
      defs.push(
        `<clipPath id="${clipId}"><rect x="${x}" y="${y}" width="${w}" height="${h}"/></clipPath>`
      );
    }
    attrs.push(`clip-path="url(#${clipId})"`);
  }

  const content =
    childContents.length > 0 ? `<g ${attrs.join(' ')}>${childContents.join('')}</g>` : '';

  return { defs, content };
}

/**
 * Render frame node (nested frame)
 */
async function renderFrame(store, node, { x, y, w, h, originX, originY }) {
  const defs = [];
  const contents = [];

  // Render frame background
  if (node.fill && !isTransparentFill(node.fill)) {
    const { attrs: styleAttrs, defs: styleDefs } = buildStyleAttrs(
      { ...node, stroke: null, strokeWidth: null },
      { includeStroke: false }
    );
    defs.push(...styleDefs);

    const radius = getNumericRadius(node);
    if (radius > 0) {
      contents.push(
        `<rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${round(h)}" rx="${round(radius)}" ry="${round(radius)}" ${styleAttrs}/>`
      );
    } else {
      contents.push(
        `<rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${round(h)}" ${styleAttrs}/>`
      );
    }
  }

  // Render children
  const children = store.api.getChildren(node.id) || [];
  const frameBox = store.api.getNodeBox(node);

  for (const child of children) {
    const result = await renderNode(store, child, { originX, originY });
    if (result.defs) defs.push(...result.defs);
    if (result.content) contents.push(result.content);
  }

  const content = contents.length > 0 ? `<g>${contents.join('')}</g>` : '';

  return { defs, content };
}

export default node2SVG;
